import styled from '@emotion/styled'
import { Button, Form, Input, message, Slider } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo, useState } from 'react'
import { useAccount, useBalance } from 'wagmi'

import CopyCode from '@/assets/copy-code-light.png'
import ToolTipCus from '@/components/Tooltips'
import { MAXDAYS, MINDAYS } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStakeRegisterTransParamsInterface } from '@/types/trans-params'
import {
  checkAmountUnit,
  checkMaxFeeRate,
  fromWei,
  fromWei1,
  handleCopyCode,
  isNumber,
} from '@/utils'
import { useIwan } from '@/utils/useWebSocket'

export default function StepTwo({
  tarnsParam,
  setStep,
  setTransParam,
}: {
  setStep: (val: number) => void
  tarnsParam: PosStakeRegisterTransParamsInterface
  setTransParam: Dispatch<SetStateAction<PosStakeRegisterTransParamsInterface>>
}) {
  const [form] = Form.useForm()
  const { address } = useAccount()
  const { data, status } = useBalance({ address })
  const { theme } = useLocalStorage()
  const isDark = useMemo(() => theme === 'dark', [theme])
  const { data: slotCount } = useIwan(['getSlotCount'], ['wan'], true)
  const { data: slotTime } = useIwan(['getSlotTime'], ['wan'], true)

  const [isAcceptDelegation, setIsAcceptDelegation] = useState<boolean>(
    tarnsParam.isAcceptDelegation,
  )
  const [lockTime, setLockTime] = useState<number>(
    tarnsParam.lockEpochs || MINDAYS,
  )

  const btnCln = clsx('w-full flex justify-around mt-[20px]', {
    'btn1-light': !isDark,
    'btn1-dark': isDark,
  })

  const balanceFormatted = useMemo(() => {
    return data && status === 'success' ? fromWei(data.value.toString(10)) : '0'
  }, [data, status])

  const balance = useMemo(() => {
    return data && status === 'success'
      ? fromWei1(data.value.toString(10))
      : '0'
  }, [data, status])

  const handleSliderChange = (value: number) => setLockTime(value)

  const checkPublicKey1 = async (_: any, value: string) => {
    if (value === undefined) {
      return Promise.reject(new Error('Invalid public key'))
    }
    if (String(value).startsWith('0x') && value.length === 132) {
      return Promise.resolve()
    } else {
      return Promise.reject(new Error('Invalid public key'))
    }
  }

  const checkPublicKey2 = async (_: any, value: string) => {
    if (value === undefined) {
      return Promise.reject(new Error('Invalid public key'))
    }
    if (String(value).startsWith('0x') && value.length === 130) {
      return Promise.resolve()
    } else {
      return Promise.reject(new Error('Invalid public key'))
    }
  }

  const checkFeeRate = async (_: any, value: any) => {
    const maxFee = form.getFieldValue('maxFeeRate') || 0
    try {
      if (!isNumber(value) || Number(value) > Number(maxFee)) {
        return Promise.reject(new Error('Invalid fee rate'))
      }
      return checkMaxFeeRate(_, value)
    } catch {
      return Promise.reject(new Error('Invalid fee rate'))
    }
  }

  const checkAmount = async (_: any, value: any) => {
    if (value === undefined || !checkAmountUnit(value)) {
      return Promise.reject(new Error('Invalid amount'))
    }
    if (new BigNumber(value).minus(10000).lt(0)) {
      return Promise.reject(new Error('Minimum amount should be 10000 WAN'))
    }
    if (new BigNumber(value).minus(balance).gte(0)) {
      return Promise.reject(
        new Error('Please make sure you have sufficient balance'),
      )
    }
    return Promise.resolve()
  }

  const handleNext = () => {
    form
      .validateFields()
      .then(() => {
        if (new BigNumber(balance).minus(form.getFieldValue('amount')).lte(0)) {
          message.error('The cost exceeds the balance. Please try again')
          return
        }
        setTransParam({
          secPk: form.getFieldValue('publicKey1'),
          bn256Pk: form.getFieldValue('publicKey2'),
          lockEpochs: isNaN(slotCount * slotTime)
            ? lockTime
            : (lockTime * 24 * 3600) / (slotCount * slotTime),
          isAcceptDelegation,
          feeRate: isAcceptDelegation ? form.getFieldValue('feeRate') : 100,
          maxFeeRate: isAcceptDelegation
            ? form.getFieldValue('maxFeeRate')
            : 100,
          amount: form.getFieldValue('amount'),
        })
        setStep(12)
      })
      .catch((err: any) => console.log(err))
  }

  return (
    <div
      className={clsx('flex-1 stwoTwoSty', {
        'stwoTwoSty-dark': isDark,
        'stwoTwoSty-light': !isDark,
      })}
    >
      <div className="border-b-1 border-b-light-555 dark:border-b-dark-666">
        <p className="text-3xl text-light-111 mb-[15px] dark:text-dark-111">
          Register & Deploy Node
        </p>
      </div>
      <div className="flex mt-[15px] dark:text-white ">
        <div className="w-[10px] mr-4">1.</div>
        <div className="mr-2 font-[16px] flex flex-col gap-2 overflow-auto whitespace-pre-wrap break-words">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-light-0 font-inter dark:text-white">
              Run the Deployment Script
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('generate')}
            />
          </div>
          <pre id="generate" className="mb-0">
            <code className="text-light-111 whitespace-pre-wrap break-words dark:text-dark-111">
              wget
              https://raw.githubusercontent.com/wanchain/scripts/main/pos/mainnet/deployMainnetValidator.sh
              && chmod +x deployMainnetValidator.sh &&
              ./deployMainnetValidator.sh
            </code>
          </pre>
          <span className="text-[14px] font-semibold text-light-0 font-inter dark:text-white">
            The script will prompt you to enter:
          </span>
          <ul className="list-disc pl-8 space-y-2 text-[14px] dark:text-dark-0">
            <li>Validator name</li>
            <li>Password for your validator address</li>
          </ul>
          <span>
            When you see{' '}
            <span className="font-semibold">"Validator Start Success"</span>,
            the node has successfully started and is syncing. You may monitor
            your validator status on{' '}
            <a
              className="dark:text-dark-111"
              target="_blank"
              rel="noreferrer"
              href={'https://wanstats.io'}
            >
              wanstats.io
            </a>
            .
          </span>
          <div className="border-1 border-light-555 dark:border-dark-666 py-[10px] rounded-2xl px-[20px]">
            <div className="font-semibold mb-2">Important:</div>
            <ul className="list-disc pl-8 space-y-2 text-[14px] dark:text-dark-0">
              <li>
                Back up your password securely. It cannot be recovered if lost.
              </li>
              <li>
                After execution, the script will output:
                <ul className="list-disc pl-8 space-y-2">
                  <li>Validator Address</li>
                  <li>Public Key 1</li>
                  <li>Public Key 2</li>
                  <li>Keystore JSON</li>
                </ul>
              </li>
            </ul>
            <div>
              You must{' '}
              <span className="font-semibold">
                back up all of this information
              </span>
              . These files + password are required to restore your node in case
              of data loss.
            </div>
          </div>
          <div className="mt-[10px] rounded-2xl py-4 px-6 bg-light-888 dark:bg-dark-333 dark:text-dark-0">
            <div className="font-semibold">Note:</div>
            <div>
              The current validator deployment script uses{' '}
              <span className="font-semibold">full mode</span>. If you prefer to
              run it in <span className="font-semibold">archive mode</span>,
              please visit{' '}
              <a
                className="dark:text-dark-111"
                target="_blank"
                rel="noreferrer"
                href={'https://docs.wanchain.org'}
              >
                docs.wanchain.org
              </a>{' '}
              for more information.
            </div>
          </div>
        </div>
      </div>
      <div className="flex mt-[15px] dark:text-white">
        <div className="w-[10px] mr-4">2.</div>
        <div className="font-[16px] flex flex-col gap-2">
          <span className="font-semibold text-light-0 font-inter dark:text-white">
            Register Your Validator
          </span>
          <span className="dark:text-dark-0">
            Fill in the registration. The two keys were generated in Step 1
          </span>
        </div>
      </div>
      <div className="ml-[10px] border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <span className="dark:text-white">Validator Account</span>
        <InputContainer className="mt-[10px] flex flex-col w-full">
          <Form form={form}>
            <Form.Item
              initialValue={tarnsParam.secPk || ''}
              name="publicKey1"
              rules={[{ required: true, validator: checkPublicKey1 }]}
            >
              <Input
                autoComplete="off"
                addonBefore="Public Key 1"
                placeholder="Enter public key 1"
              />
            </Form.Item>
          </Form>
          <Form form={form}>
            <Form.Item
              initialValue={tarnsParam.bn256Pk || ''}
              name="publicKey2"
              rules={[{ required: true, validator: checkPublicKey2 }]}
            >
              <Input
                autoComplete="off"
                addonBefore="Public Key 2"
                placeholder="Enter public key 2"
              />
            </Form.Item>
          </Form>
          <div className="flex justify-center items-center">
            <div className="bg-light-101 text-light-111 w-full max-w-[100px] border-l-1 border-t-1 border-b-1 border-light-105 p-[5px] h-[35px] text-center rounded-tl-[10px] rounded-bl-[10px] dark:bg-transparent dark:border-dark-999">
              Lock Period
            </div>
            <SliderContainer className="flex w-full border-1 border-light-105 p-[5px] h-[35px] gap-2 dark:border-dark-999">
              <div className="w-full">
                <Slider
                  step={1}
                  min={MINDAYS}
                  max={MAXDAYS}
                  className="top-[-4px]"
                  defaultValue={tarnsParam.lockEpochs || lockTime}
                  tooltip={{ open: false }}
                  onChange={handleSliderChange}
                />
              </div>
              <div className="whitespace-nowrap text-light-111 dark:text-dark-111">
                {lockTime} Days
              </div>
            </SliderContainer>
          </div>
        </InputContainer>
        <div className="flex gap-2 items-center justify-center mt-4">
          <div className="flex gap-2 mt-4 items-center h-full justify-center">
            <div className=" dark:text-white">Accept Delegation</div>
          </div>
          <div className="flex-1 flex justify-around items-center mt-[10px] gap-[10px]">
            <Button
              onClick={() => setIsAcceptDelegation(true)}
              className={
                'w-[48%] rounded-3xl h-[25px] text-[14px] leading-[6px] ' +
                (isAcceptDelegation
                  ? isDark
                    ? 'bg-dark-101 text-dark-111 border-dark-999'
                    : 'bg-light-888 text-dark-111'
                  : isDark
                    ? 'bg-transparent text-dark-0 border-dark-999'
                    : 'bg-white text-light-444')
              }
            >
              Yes
            </Button>
            <Button
              onClick={() => setIsAcceptDelegation(false)}
              className={
                'w-[48%] rounded-3xl h-[25px] btn text-[14px] leading-[6px] ' +
                (isAcceptDelegation
                  ? isDark
                    ? 'bg-transparent text-dark-0 border-dark-999'
                    : 'bg-white'
                  : isDark
                    ? 'bg-dark-101 text-dark-111 border-dark-999'
                    : 'bg-light-888')
              }
            >
              No
            </Button>
          </div>
        </div>

        {isAcceptDelegation && (
          <InputContainer className="mt-[19px] flex flex-col">
            <Form form={form}>
              <Form.Item
                initialValue={tarnsParam.maxFeeRate || ''}
                name="maxFeeRate"
                rules={[{ required: true, validator: checkMaxFeeRate }]}
              >
                <Input
                  autoComplete="off"
                  suffix="%"
                  addonBefore={
                    <div className="pr-4">
                      Max Fee (%)
                      <ToolTipCus
                        title={
                          'The Max Fee represents the absolute upper limit for the Delegation Fee that a validator node can set. Note: Once the Max Fee is set, it is immutable and cannot be modified.'
                        }
                      />
                    </div>
                  }
                  placeholder="10"
                />
              </Form.Item>
            </Form>
            <Form form={form}>
              <Form.Item
                initialValue={tarnsParam.feeRate || ''}
                name="feeRate"
                rules={[{ required: true, validator: checkFeeRate }]}
              >
                <Input
                  autoComplete="off"
                  suffix="%"
                  addonBefore={
                    <div className="pr-4">
                      Delegation Fee (%)
                      <ToolTipCus
                        title={
                          'The current commission rate taken from daily delegating rewards. Note: Increases are limited to a maximum of 1% per day and cannot exceed the Max Fee. Decreases are unrestricted.'
                        }
                      />
                    </div>
                  }
                  placeholder="Up to two decimal places"
                />
              </Form.Item>
            </Form>
          </InputContainer>
        )}
      </div>
      <div className="ml-[10px] border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <div className="dark:text-white">My Account</div>
        <InputContainer
          className={clsx('mt-[10px] flex flex-col gap-[19px]', {
            'inputDisabled-dark': isDark,
            'inputDisabled-light': !isDark,
          })}
        >
          <Input
            className="disabledSty"
            disabled
            addonBefore="Address"
            value={address}
          />
          <Input
            className="disabledSty"
            addonBefore="Balance"
            disabled
            value={balanceFormatted}
          />

          <Form form={form}>
            <Form.Item
              name="amount"
              rules={[{ required: true, validator: checkAmount }]}
            >
              <Input
                autoComplete="off"
                addonBefore="Amount"
                placeholder={isAcceptDelegation ? '50,000' : '10,000'}
              />
            </Form.Item>
          </Form>
        </InputContainer>
      </div>
      <div className={btnCln}>
        <Button
          onClick={() => setStep(0)}
          className="w-[49%] rounded-2xl h-[60px] text-[18px]"
        >
          Previous
        </Button>
        <Button
          className="w-[49%] rounded-2xl h-[60px] btn text-[18px]"
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

const InputContainer = styled.div`
  .ant-input-group-addon {
    color: #0f68aa;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  .ant-input {
    height: 35px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .ant-input:hover {
    border-color: none;
  }
`

const SliderContainer = styled.div`
  border-left: none !important;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
`
